import { HEATING_FUEL_ORDER, UTILITY_SOURCE_ORDER, type HeatingFuel, type UtilitySource } from '../../domain/descriptors';
import type { PropertyFactsPatch } from '../../domain/inspection';
import { useT } from '../../i18n/i18n';
import { descriptorLabel, heatingFuelLabel, labelWithTag, utilitySourceLabel } from '../../i18n/labels';
import { PickerRow } from '../../ui/components';

// NY F1 Round 2 (CK-01 · 197-5.8(d) · 197-5.10(a)(1); OD-1 = A1): the three property facts, asked
// ONCE — at setup, or later on the details screen for a record written before the field existed.
// Three picker rows (Owner-selected design 2026-09-14), one choice each; nothing is pre-selected and
// nothing is inferred. Choosing the chosen option again clears the answer back to "not asked" (never
// to Unknown). "Unknown" is an option the inspector chooses deliberately: lawful for the two utility
// sources, recorded-but-unresolved for the heating fuel (src/domain/descriptors.ts).
export function PropertyFactRows(props: {
  heatingFuel: HeatingFuel | null;
  waterSupplySource: UtilitySource | null;
  wasteDisposalSource: UtilitySource | null;
  disabled?: boolean;
  onChange: (patch: PropertyFactsPatch) => void;
  testId: string;
}) {
  const { t, locale } = useT();
  const disabled = props.disabled === true;
  const row = <K extends string>(key: 'heatingFuel' | 'waterSupplySource' | 'wasteDisposalSource', options: readonly K[], current: K | null, labelFor: (k: K) => string) => (
    <PickerRow
      label={descriptorLabel(t, key)}
      value={current}
      options={options.map((option) => ({ key: option, label: labelFor(option), testId: `${props.testId}-${key}-${option}` }))}
      onChange={(next) => {
        if (disabled) return;
        props.onChange({ [key]: next } as PropertyFactsPatch);
      }}
      disabled={disabled}
      testId={`${props.testId}-${key}`}
      valueTestId={`${props.testId}-${key}-value`}
    />
  );
  return (
    <div className="form-rows" data-testid={props.testId}>
      {row('heatingFuel', HEATING_FUEL_ORDER, props.heatingFuel, (fuel) => heatingFuelLabel(t, fuel))}
      {row('waterSupplySource', UTILITY_SOURCE_ORDER, props.waterSupplySource, (source) => utilitySourceLabel(t, 'waterSupplySource', source))}
      {row('wasteDisposalSource', UTILITY_SOURCE_ORDER, props.wasteDisposalSource, (source) => utilitySourceLabel(t, 'wasteDisposalSource', source))}
      <p className="card-note form-rows-note">
        {labelWithTag(locale, t('facts.title'), t('common.optional'))} · {t('facts.hint')}
      </p>
    </div>
  );
}
